class LreGame extends HTMLElement {
  async connectedCallback() {
    if (this.dataset.ready === 'true' || this.initializing) return;
    this.initializing = true;
    try {
      // Liquid supplies the CDN version so updates do not reuse an old engine.
      this.engine = await import(this.dataset.engineUrl);
      if (typeof this.engine.revealCard !== 'function') {
        throw new Error('Update lre-game-engine.js in this Shopify theme, then reload the page.');
      }
      this.copy = JSON.parse(this.querySelector('[data-copy]').textContent);
      this.cards = this.readCards();
      this.boards = this.readBoards();
      this.soundEnabled = false;
      this.bindEvents();
      this.dataset.ready = 'true';
      this.start();
    } catch (error) {
      const status = this.querySelector('[data-status]');
      if (status) status.textContent = error.message;
      console.error('[Lotería] Unable to initialize the game:', error);
    } finally {
      this.initializing = false;
    }
  }

  readCards() {
    const readRecords = (selector) => [...this.querySelectorAll(selector)].map((item) => ({
      id: (item.dataset.id || '').trim(),
      label: (item.dataset.label || '').trim(),
      image: item.dataset.image || '',
      backImage: item.dataset.backImage || '',
    })).filter((card) => card.id && !['36', '37'].includes(card.id));
    const defaults = readRecords('[data-default-card-record]');
    const defaultsById = new Map(defaults.map((card) => [card.id, card]));
    const configured = readRecords('[data-card-record]').map((card) => {
      const bundled = defaultsById.get(card.id);
      return {
        id: card.id,
        label: card.label || bundled?.label || '',
        image: card.image || bundled?.image || '',
        backImage: card.backImage || bundled?.backImage || '',
      };
    }).filter((card) => card.label && card.image && card.backImage);
    // Preserve a configured catalog when usable; recover the bundled catalog otherwise.
    return configured.length >= 16 ? configured : defaults;
  }

  readBoards() {
    const cardIds = new Set(this.cards.map((card) => card.id));
    const boards = [...this.querySelectorAll('[data-board-record]')].map((item) => ({
      id: item.dataset.id.trim(),
      label: item.dataset.label.trim(),
      image: item.dataset.image,
      cells: item.dataset.cells.split(',').map((cell) => cell.trim()).filter(Boolean),
    })).filter((board) => board.id && board.cells.length === 16 && board.cells.every((id) => cardIds.has(id)));
    return boards;
  }

  bindEvents() {
    this.addEventListener('click', (event) => {
      const cell = event.target.closest('[data-cell]');
      if (cell) return this.resolveCell(Number(cell.dataset.cell));
      if (event.target.closest('[data-flip]')) return this.flip();
      if (event.target.closest('[data-absent]')) return this.resolveAbsent();
      if (event.target.closest('[data-next]')) return this.advance();
      if (event.target.closest('[data-restart]')) return this.start();
      if (event.target.closest('[data-sound]')) return this.toggleSound();
    });
  }

  disconnectedCallback() {
    clearTimeout(this.flipTimer);
  }

  start() {
    clearTimeout(this.flipTimer);
    this.flipping = false;
    this.displayedCard = null;
    try {
      const availableBoards = this.state && this.boards.length > 1
        ? this.boards.filter((board) => board.id !== this.state.board.id)
        : this.boards;
      this.state = this.engine.createGame({ cards: this.cards, boards: availableBoards, maxTurns: 5 });
      this.render();
    } catch (error) {
      this.querySelector('[data-status]').textContent = error.message;
    }
  }

  flip() {
    if (this.flipping || this.state.revealed || this.state.finished) return;
    this.flipping = true;
    this.querySelector('[data-flip]').disabled = true;
    this.querySelector('[data-card]').classList.add('is-revealed');
    const finish = () => {
      this.flipping = false;
      this.state = this.engine.revealCard(this.state).state;
      this.render();
      this.feedback(`${this.copy.revealed.replace('{{ name }}', this.engine.currentCard(this.state).label)} ${this.copy.choose}`, 'neutral', 'reveal');
      this.querySelector('[data-absent]').focus({ preventScroll: true });
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
    else this.flipTimer = setTimeout(finish, 650);
  }

  resolveCell(index) {
    if (this.flipping || !this.state.revealed) return;
    const outcome = this.engine.chooseCell(this.state, index);
    this.state = outcome.state;
    if (outcome.result === 'wrong') this.feedback(this.copy.wrong, 'error', 'error');
    if (outcome.result === 'match') this.feedback(this.copy.match, 'success', 'success');
    this.render();
  }

  resolveAbsent() {
    if (this.flipping || !this.state.revealed) return;
    const outcome = this.engine.chooseAbsent(this.state);
    this.state = outcome.state;
    if (outcome.result === 'present') this.feedback(this.copy.present, 'error', 'error');
    if (outcome.result === 'absent') this.feedback(this.copy.absent, 'success', 'success');
    this.render();
  }

  advance() {
    this.state = this.engine.nextTurn(this.state).state;
    this.render();
    this.querySelector('[data-flip]').focus({ preventScroll: true });
  }

  feedback(message, tone, sound) {
    const status = this.querySelector('[data-status]');
    status.textContent = message;
    status.dataset.tone = tone;
    this.playSound(sound);
  }

  toggleSound() {
    this.soundEnabled = !this.soundEnabled;
    const button = this.querySelector('[data-sound]');
    button.setAttribute('aria-pressed', String(this.soundEnabled));
    button.querySelector('span').textContent = this.soundEnabled ? this.copy.soundOn : this.copy.soundOff;
    if (this.soundEnabled) this.playSound('reveal');
  }

  playSound(type) {
    if (!this.soundEnabled || !window.AudioContext) return;
    const context = this.audioContext || (this.audioContext = new AudioContext());
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const frequencies = { reveal: 440, success: 660, error: 180 };
    oscillator.frequency.value = frequencies[type] || 440;
    oscillator.type = type === 'error' ? 'square' : 'sine';
    gain.gain.setValueAtTime(0.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.08, context.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.14);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.15);
  }

  render() {
    const card = this.engine.currentCard(this.state);
    this.querySelector('[data-turn]').textContent = this.copy.turn
      .replace('{{ current }}', String(this.state.turn + 1))
      .replace('{{ total }}', String(this.state.maxTurns));
    this.querySelector('[data-progress]').style.setProperty('--progress', `${((this.state.turn + 1) / this.state.maxTurns) * 100}%`);
    const cardImage = this.querySelector('[data-card-image]');
    cardImage.hidden = !card.image;
    if (card.image) {
      cardImage.src = card.image;
      cardImage.alt = card.label;
    }

    this.querySelector('[data-front-placeholder]').textContent = card.image ? '' : card.label;
    const cardElement = this.querySelector('[data-card]');
    const inner = this.querySelector('[data-card-inner]');
    if (this.displayedCard !== card) {
      // Reset without animating the previous front back into the new definition.
      inner.style.transition = 'none';
      cardElement.classList.remove('is-revealed');
      void inner.offsetWidth;
      inner.style.transition = '';
      this.displayedCard = card;
    }
    cardElement.classList.toggle('is-revealed', this.state.revealed);
    const backImage = this.querySelector('[data-card-back]');
    backImage.hidden = !card.backImage;
    if (card.backImage) backImage.src = card.backImage;
    this.querySelector('[data-back-placeholder]').hidden = Boolean(card.backImage);
    this.querySelector('[data-back-face]').setAttribute('aria-hidden', String(this.state.revealed));
    this.querySelector('[data-front-face]').setAttribute('aria-hidden', String(!this.state.revealed));
    const flipButton = this.querySelector('[data-flip]');
    flipButton.hidden = this.state.revealed || this.state.finished;
    flipButton.disabled = this.flipping;
    this.renderBoard();
    this.querySelector('[data-absent]').hidden = !this.state.revealed || this.state.resolved || this.state.finished;
    this.querySelector('[data-next]').hidden = !this.state.resolved || this.state.finished;
    this.querySelector('[data-end]').hidden = !this.state.finished;
    this.querySelector('[data-play]').hidden = this.state.finished;
    if (!this.state.revealed) this.feedback(this.copy.readDefinition, 'neutral');
    else if (!this.state.resolved && !this.querySelector('[data-status]').textContent) this.feedback(this.copy.choose, 'neutral');
  }

  renderBoard() {
    const boardImage = this.querySelector('[data-board-image]');
    boardImage.hidden = !this.state.board.image;
    if (this.state.board.image) {
      boardImage.src = this.state.board.image;
      boardImage.alt = this.state.board.label;
    }
    this.querySelector('[data-board]').classList.toggle('lre-game__board--image', Boolean(this.state.board.image));
    const cardsById = new Map(this.cards.map((card) => [card.id, card]));
    this.querySelector('[data-cells]').innerHTML = this.state.board.cells.map((id, index) => {
      const card = cardsById.get(id) || { label: id, image: '' };
      const marked = this.state.marked.includes(index);
      const hasImage = !this.state.board.image && card.image;
      const background = hasImage ? ` style="--cell-image:url('${card.image}')"` : '';
      return `<button type="button" data-cell="${index}" class="lre-game__cell${marked ? ' is-marked' : ''}${hasImage ? ' has-image' : ''}" aria-label="${this.escape(card.label)}" aria-pressed="${marked}"${!this.state.revealed || this.state.resolved || this.state.finished ? ' disabled' : ''}${background}><span>${this.escape(card.label)}</span></button>`;
    }).join('');
  }

  escape(value) {
    const node = document.createElement('span');
    node.textContent = value;
    return node.innerHTML;
  }
}

if (!customElements.get('lre-game')) customElements.define('lre-game', LreGame);
